import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import dataService from '../services/dataService';
import { useAuth } from '../context/AuthContext';
import {
    EyeIcon,
    CalendarIcon,
    PlusIcon,
    UserGroupIcon,
    MapPinIcon,
    LinkIcon,
    ClockIcon,
    CurrencyRupeeIcon,
    XMarkIcon,
    PencilSquareIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import ExecutiveMeetingTable from '../components/ExecutiveMeetingTable';

const ChapterMeetings = () => {
    const { user } = useAuth();
    const chapterLabel = user?.namingConvention?.chapterLabel || 'Chapter';
    const meetingLabel = user?.namingConvention?.meetingLabel || 'Meeting';
    const activeRole = sessionStorage.getItem('activeRole');
    const isActingAsChapterAdmin = user.role === 'chapter_admin' && activeRole !== 'member';
    const isChapterAdmin = isActingAsChapterAdmin;
    const isOrgAdmin = user.role === 'admin';
    const isAdmin = isChapterAdmin || isOrgAdmin;

    const navigate = useNavigate();
    const { orgCode } = useParams();
    const orgPrefix = orgCode || user?.subDomain || user?.organizationName || '';
    const shouldBlock = !isAdmin && user?.orgSettings?.chapter_meeting_display === true;

    useEffect(() => {
        if (shouldBlock) {
            navigate(`/${orgPrefix}/userDashboard`);
            toast.error("Access denied");
        }
    }, [shouldBlock, navigate, orgPrefix]);

    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeTab, setTimeTab] = useState('upcoming');
    const [currentPage, setCurrentPage] = useState(1);

    // Modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isRegListModalOpen, setIsRegListModalOpen] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [registrations, setRegistrations] = useState([]);
    const [loadingRegs, setLoadingRegs] = useState(false);
    const [chapters, setChapters] = useState([]);

    // Form State
    const initialForm = {
        title: '',
        description: '',
        meetingDate: '',
        cutoffDate: '',
        startTime: '',
        endTime: '',
        mode: 'In-Person',
        meetingLink: '',
        location: '',
        charges: '0',
        paymentLink: '',
        status: 'Scheduled'
    };
    const [formData, setFormData] = useState(initialForm);
    const [isEditing, setIsEditing] = useState(false);

    // Visitor Modal State
    const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(false);
    const [visitorForm, setVisitorForm] = useState({
        name: '',
        email: '',
        contact_number: '',
        company_name: '',
        chapter: '',
        payment_confirmed: false
    });

    useEffect(() => {
        fetchMeetings();
        fetchChapters();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [timeTab]);

    const fetchChapters = async () => {
        try {
            const res = await dataService.getChapters();
            if (res.data && res.data.success) {
                setChapters(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch chapters', error);
        }
    };

    const fetchMeetings = async () => {
        setLoading(true);
        try {
            const response = await dataService.getChapterMeetings();
            setMeetings(response.data.data);
        } catch (error) {
            toast.error('Failed to fetch chapter meetings');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrUpdate = async (e) => {
        e.preventDefault();
        try {
            const today = new Date().toISOString().split('T')[0];
            if (formData.meetingDate < today) {
                toast.warning('Past date selection for meeting is not allowed. Please select current or future date.');
                return;
            }
            if (formData.cutoffDate < today) {
                toast.warning('Past date selection for cutoff is not allowed. Please select current or future date.');
                return;
            }

            if (formData.meetingDate && formData.cutoffDate) {
                if (formData.cutoffDate > formData.meetingDate) {
                    toast.warning('Cutoff Date cannot be after the Meeting Date.');
                    return;
                }
            }

            if (isEditing) {
                await dataService.updateChapterMeeting(selectedMeeting.id, formData);
                toast.success('Meeting updated successfully');
            } else {
                await dataService.createChapterMeeting(formData);
                toast.success('Meeting scheduled successfully');
            }
            setIsCreateModalOpen(false);
            setFormData(initialForm);
            setIsEditing(false);
            fetchMeetings();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save meeting');
        }
    };

    const handleRegister = async (meetingId) => {
        try {
            await dataService.registerChapterMeeting(meetingId);
            toast.success('Registered successfully');
            fetchMeetings();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Registration failed');
        }
    };

    const handleViewRegs = async (meeting) => {
        setSelectedMeeting(meeting);
        setIsRegListModalOpen(true);
        setLoadingRegs(true);
        try {
            const response = await dataService.getChapterMeetingRegistrations(meeting.id);
            setRegistrations(response.data.data);
        } catch (error) {
            toast.error('Failed to fetch registrations');
        } finally {
            setLoadingRegs(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this meeting?')) return;
        try {
            await dataService.deleteChapterMeeting(id);
            toast.success('Meeting deleted');
            fetchMeetings();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete meeting');
        }
    };

    const filteredMeetings = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        return meetings.filter(m => {
            const mDate = new Date(m.meeting_date);
            return timeTab === 'upcoming' ? mDate >= now : mDate < now;
        }).sort((a, b) => {
            const dateA = new Date(a.meeting_date);
            const dateB = new Date(b.meeting_date);
            return timeTab === 'upcoming' ? dateA - dateB : dateB - dateA;
        });
    }, [meetings, timeTab]);

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filteredMeetings.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedMeetings = filteredMeetings.slice(startIndex, endIndex);

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeString)) {
            const [hours, minutes] = timeString.split(':');
            const d = new Date();
            d.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        const d = new Date(timeString);
        if (isNaN(d)) return '-';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleAddVisitor = async (e) => {
        e.preventDefault();
        try {
            await dataService.addVisitorToChapterMeeting(selectedMeeting.id, visitorForm);
            toast.success('Visitor added successfully');
            setIsVisitorModalOpen(false);
            setVisitorForm({ name: '', email: '', contact_number: '', company_name: '', chapter: '', payment_confirmed: false });
            // Refresh registrations/visitors list if we display them
            handleViewRegs(selectedMeeting);
        } catch (error) {
            console.error(error);
            toast.error('Failed to add visitor');
        }
    };

    const handleEdit = (meeting) => {
        setSelectedMeeting(meeting);
        setFormData({
            title: meeting.title,
            description: meeting.description,
            meetingDate: meeting.meeting_date.split('T')[0], // Extract YYYY-MM-DD
            cutoffDate: meeting.cutoff_date ? meeting.cutoff_date.split('T')[0] : '',
            startTime: meeting.start_time ? meeting.start_time.slice(0, 5) : '',
            endTime: meeting.end_time ? meeting.end_time.slice(0, 5) : '',
            mode: meeting.mode,
            meetingLink: meeting.meeting_link || '',
            location: meeting.location || '',
            charges: meeting.charges,
            paymentLink: meeting.payment_link || '',
            status: meeting.status
        });
        setIsEditing(true);
        setIsCreateModalOpen(true);
    };

    if (shouldBlock) {
        return null;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">{chapterLabel} {meetingLabel}s</h1>
                    <p className="text-slate-500 font-medium mt-1">Connect, collaborate, and grow with your chapter.</p>
                </div>
                {isChapterAdmin && (
                    <button
                        onClick={() => {
                            setFormData(initialForm);
                            setIsEditing(false);
                            setIsCreateModalOpen(true);
                        }}
                        className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-2xl font-bold shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all active:scale-95"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Schedule Chapter Meeting
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b border-slate-100 mb-8">
                {[
                    { id: 'upcoming', label: 'Upcoming Meetings', icon: CalendarIcon },
                    { id: 'previous', label: 'Previous Meetings', icon: ClockIcon }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setTimeTab(tab.id)}
                        className={twMerge(
                            "flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all border-b-2",
                            timeTab === tab.id
                                ? "text-primary-600 border-primary-600 bg-primary-50/30"
                                : "text-slate-400 border-transparent hover:text-slate-600"
                        )}
                    >
                        <tab.icon className="w-5 h-5" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                    <p className="text-slate-500 font-bold">Loading meetings...</p>
                </div>
            ) : filteredMeetings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-200">
                    <CalendarIcon className="w-16 h-16 text-slate-200 mb-4" />
                    <h3 className="text-xl font-bold text-slate-900">No {timeTab} meetings found</h3>
                    <p className="text-slate-400 font-medium">There are no chapter meetings scheduled at this time.</p>
                </div>
            ) : (
                <>
                    <ExecutiveMeetingTable
                        meetings={paginatedMeetings}
                        onView={(m) => {
                            setSelectedMeeting(m);
                            setIsDetailsModalOpen(true);
                        }}
                        onRSVP={handleRegister}
                        onDelete={handleDelete}
                        onManageMembers={handleViewRegs}
                        onEdit={handleEdit}
                        onAddVisitor={(meeting) => {
                            setSelectedMeeting(meeting);
                            setIsVisitorModalOpen(true);
                        }}
                        context="chapter"
                    />

                    {/* Pagination Controls */}
                    <div className="flex flex-col md:flex-row items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
                        <div className="text-sm font-semibold text-slate-600">
                            Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredMeetings.length)}</span> of{' '}
                            <span className="font-black text-slate-900">{filteredMeetings.length}</span> {filteredMeetings.length === 1 ? 'meeting' : 'meetings'}
                        </div>

                        {filteredMeetings.length > itemsPerPage && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                        : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-1">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                        ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                        : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                            return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                        : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Schedule/Edit Modal */}
            <Transition.Root show={isCreateModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsCreateModalOpen(false)}>
                    <Transition.Child as={Fragment}>
                        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment}>
                                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-[40px] shadow-2xl overflow-hidden p-8 md:p-12">
                                    <div className="flex justify-between items-center mb-8">
                                        <Dialog.Title className="text-3xl font-black text-slate-900">
                                            {isEditing ? 'Edit Chapter Meeting' : 'Schedule Chapter Meeting'}
                                        </Dialog.Title>
                                        <button onClick={() => setIsCreateModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                            <XMarkIcon className="w-6 h-6 text-slate-400" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleCreateOrUpdate} className="space-y-4">
                                        {/* Status Dropdown - Only for Edit Mode */}
                                        {isEditing && (
                                            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                                                <label className="block text-xs font-black text-amber-900 uppercase tracking-widest mb-2">Meeting Status</label>
                                                <select
                                                    value={formData.status}
                                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                                    className="w-full px-4 py-2 bg-white border border-amber-200 rounded-lg text-sm font-bold text-amber-900 focus:ring-2 focus:ring-amber-400 outline-none"
                                                >
                                                    <option value="Scheduled">Scheduled</option>
                                                    <option value="Completed">Completed</option>
                                                    <option value="Not Completed">Not Completed</option>
                                                </select>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2">
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Meeting Subject *</label>
                                                <input
                                                    required
                                                    disabled={isOrgAdmin && !isChapterAdmin} // Admin Read-Only logic if needed (though admin shouldn't open this modal usually, or if they do, read-only?)
                                                    type="text"
                                                    value={formData.title}
                                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                                    placeholder="e.g. Monthly Strategy Review"
                                                />
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Meeting Mode</label>
                                                <div className="flex gap-2">
                                                    {['In-Person', 'Virtual'].map(m => (
                                                        <button
                                                            key={m}
                                                            type="button"
                                                            onClick={() => setFormData({ ...formData, mode: m })}
                                                            className={twMerge(
                                                                "flex-1 py-2 rounded-xl text-xs font-black transition-all border",
                                                                formData.mode === m
                                                                    ? "bg-primary-50 border-primary-600 text-primary-600"
                                                                    : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                                                            )}
                                                        >
                                                            {m}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {formData.mode === 'Virtual' ? (
                                                <div className="md:col-span-2">
                                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Meeting Link *</label>
                                                    <input
                                                        required={formData.mode === 'Virtual'}
                                                        type="url"
                                                        value={formData.meetingLink}
                                                        onChange={e => setFormData({ ...formData, meetingLink: e.target.value })}
                                                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                        placeholder="https://zoom.us/j/..."
                                                    />
                                                </div>
                                            ) : (
                                                <div className="md:col-span-2">
                                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Meeting Place *</label>
                                                    <input
                                                        required={formData.mode === 'In-Person'}
                                                        type="text"
                                                        value={formData.location}
                                                        onChange={e => setFormData({ ...formData, location: e.target.value })}
                                                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                        placeholder="Hotel Royal, Conference Hall A"
                                                    />
                                                </div>
                                            )}

                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Date *</label>
                                                <input
                                                    required
                                                    type="date"
                                                    min={new Date().toISOString().split('T')[0]}
                                                    value={formData.meetingDate}
                                                    onChange={e => setFormData({ ...formData, meetingDate: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Cutoff *</label>
                                                <input
                                                    required
                                                    type="date"
                                                    min={new Date().toISOString().split('T')[0]}
                                                    max={formData.meetingDate || undefined}
                                                    value={formData.cutoffDate}
                                                    onChange={e => setFormData({ ...formData, cutoffDate: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Start *</label>
                                                <input
                                                    required
                                                    type="time"
                                                    value={formData.startTime}
                                                    onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">End *</label>
                                                <input
                                                    required
                                                    type="time"
                                                    value={formData.endTime}
                                                    onChange={e => setFormData({ ...formData, endTime: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Charges (₹)</label>
                                                <input
                                                    type="number"
                                                    value={formData.charges}
                                                    onChange={e => setFormData({ ...formData, charges: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Payment Link</label>
                                                <input
                                                    type="url"
                                                    value={formData.paymentLink}
                                                    onChange={e => setFormData({ ...formData, paymentLink: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                    placeholder="Razorpay Link"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Description *</label>
                                                <textarea
                                                    required
                                                    rows={3}
                                                    value={formData.description}
                                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold resize-none focus:ring-2 focus:ring-primary-500"
                                                    placeholder="Detailed agenda..."
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            className="w-full py-4 bg-primary-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all hover:scale-[1.02] active:scale-95"
                                        >
                                            {isEditing ? 'Update Meeting' : 'Schedule Meeting'}
                                        </button>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Visit Registration Modal */}
            <Transition.Root show={isVisitorModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={() => setIsVisitorModalOpen(false)}>
                    <Transition.Child as={Fragment}>
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment}>
                                <Dialog.Panel className="w-full max-w-md bg-white rounded-[30px] shadow-2xl p-8">
                                    <div className="flex justify-between items-center mb-6">
                                        <Dialog.Title className="text-xl font-black text-slate-900">Add Visitor</Dialog.Title>
                                        <button onClick={() => setIsVisitorModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400"><XMarkIcon className="w-6 h-6" /></button>
                                    </div>
                                    <form onSubmit={handleAddVisitor} className="space-y-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Visitor Name *</label>
                                            <input required type="text" value={visitorForm.name} onChange={e => setVisitorForm({ ...visitorForm, name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email *</label>
                                            <input required type="email" value={visitorForm.email} onChange={e => setVisitorForm({ ...visitorForm, email: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contact Number *</label>
                                            <input required type="text" value={visitorForm.contact_number} onChange={e => setVisitorForm({ ...visitorForm, contact_number: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Company Name</label>
                                            <input type="text" value={visitorForm.company_name} onChange={e => setVisitorForm({ ...visitorForm, company_name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Chapter *</label>
                                            <select
                                                required
                                                value={visitorForm.chapter}
                                                onChange={e => setVisitorForm({ ...visitorForm, chapter: e.target.value })}
                                                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value="">Select Chapter</option>
                                                {chapters.map((ch) => (
                                                    <option key={ch.id} value={ch.name}>{ch.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Payment Details */}
                                        {selectedMeeting && Number(selectedMeeting.charges || 0) > 0 && (
                                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3">
                                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Payment Options</h4>
                                                <div className="flex justify-between items-center text-sm font-semibold">
                                                    <span className="text-slate-500">Charges:</span>
                                                    <span className="text-slate-900 font-bold">₹{selectedMeeting.charges}</span>
                                                </div>
                                                {selectedMeeting.payment_link && (
                                                    <div className="text-xs font-semibold">
                                                        <span className="text-slate-500 block mb-1">Payment Link:</span>
                                                        <a 
                                                            href={selectedMeeting.payment_link} 
                                                            target="_blank" 
                                                            rel="noreferrer" 
                                                            className="text-primary-600 hover:underline break-all inline-flex items-center gap-1"
                                                        >
                                                            <LinkIcon className="w-3.5 h-3.5" />
                                                            {selectedMeeting.payment_link}
                                                        </a>
                                                    </div>
                                                )}
                                                <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-slate-200">
                                                    <input
                                                        type="checkbox"
                                                        checked={visitorForm.payment_confirmed}
                                                        onChange={(e) => setVisitorForm({ ...visitorForm, payment_confirmed: e.target.checked })}
                                                        className="w-4.5 h-4.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                                    />
                                                    <span className="text-xs font-bold text-slate-700 select-none">Payment Done</span>
                                                </label>
                                            </div>
                                        )}
                                        <button type="submit" className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all">Add Visitor</button>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Registration List Modal */}
            <Transition.Root show={isRegListModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsRegListModalOpen(false)}>
                    <Transition.Child as={Fragment}>
                        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment}>
                                <Dialog.Panel className="w-full max-w-xl bg-white rounded-[40px] shadow-2xl overflow-hidden p-8">
                                    <div className="flex justify-between items-center mb-8">
                                        <Dialog.Title className="text-2xl font-black text-slate-900">
                                            Registered Members
                                        </Dialog.Title>
                                        <button onClick={() => setIsRegListModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                            <XMarkIcon className="w-6 h-6 text-slate-400" />
                                        </button>
                                    </div>

                                    {loadingRegs ? (
                                        <div className="flex justify-center py-12">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                        </div>
                                    ) : registrations.length === 0 ? (
                                        <div className="text-center py-12 text-slate-400 font-bold">No members registered yet.</div>
                                    ) : (
                                        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                            {registrations.map(reg => (
                                                <div key={reg.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                                                    <div>
                                                        <p className="font-black text-slate-900">{reg.member_name}</p>
                                                        <p className="text-xs font-bold text-slate-400">{reg.member_email}</p>
                                                    </div>
                                                    <span className={twMerge(
                                                        "text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full",
                                                        reg.type === 'Visitor' ? "text-indigo-600 bg-indigo-50" : "text-emerald-600 bg-emerald-50"
                                                    )}>
                                                        {reg.type || 'Registered'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Details Modal */}
            <Transition.Root show={isDetailsModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsDetailsModalOpen(false)}>
                    <Transition.Child as={Fragment}>
                        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment}>
                                <Dialog.Panel className="w-full max-w-lg bg-white rounded-[40px] shadow-2xl overflow-hidden p-8 md:p-10">
                                    {selectedMeeting && (
                                        <div className="space-y-6">
                                            <div className="flex justify-between items-start">
                                                <div className="space-y-1">
                                                    <h2 className="text-3xl font-black text-slate-900 leading-tight">{selectedMeeting.title}</h2>
                                                    {/* Status Badge */}
                                                    <div className="flex gap-2">
                                                        {selectedMeeting.status && (
                                                            <span className={twMerge(
                                                                "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                                                                selectedMeeting.status === 'Completed' ? "bg-green-100 text-green-700" :
                                                                    selectedMeeting.status === 'Not Completed' ? "bg-red-100 text-red-700" :
                                                                        "bg-blue-100 text-blue-700"
                                                            )}>
                                                                {selectedMeeting.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <button onClick={() => setIsDetailsModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                                    <XMarkIcon className="w-6 h-6 text-slate-400" />
                                                </button>
                                            </div>

                                            {/* Admin Actions: Edit & Add Visitor */}
                                            {isChapterAdmin && selectedMeeting && !(new Date(selectedMeeting.meeting_date) < new Date().setHours(0, 0, 0, 0)) && (
                                                <div className="grid grid-cols-2 gap-4">
                                                    <button
                                                        onClick={() => {
                                                            setIsDetailsModalOpen(false);
                                                            handleEdit(selectedMeeting);
                                                        }}
                                                        className="py-2 bg-amber-50 text-amber-700 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-amber-100 transition-all border border-amber-200"
                                                    >
                                                        <PencilSquareIcon className="w-5 h-5" />
                                                        Edit / Status
                                                    </button>
                                                    <button
                                                        onClick={() => setIsVisitorModalOpen(true)}
                                                        className="py-2 bg-indigo-50 text-indigo-700 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-100 transition-all border border-indigo-200"
                                                    >
                                                        <UserGroupIcon className="w-5 h-5" />
                                                        Add Visitor
                                                    </button>
                                                </div>
                                            )}

                                            <div className="p-6 bg-slate-50 rounded-[30px] space-y-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="p-3 bg-white rounded-2xl shadow-sm text-primary-600">
                                                        <CalendarIcon className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Meeting Date</p>
                                                        <p className="font-bold text-slate-900">{new Date(selectedMeeting.meeting_date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="p-3 bg-white rounded-2xl shadow-sm text-primary-600">
                                                        <ClockIcon className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Time Slot</p>
                                                        <p className="font-bold text-slate-900">{selectedMeeting.start_time ? formatTime(selectedMeeting.start_time) : '-'} - {selectedMeeting.end_time ? formatTime(selectedMeeting.end_time) : '-'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="p-3 bg-white rounded-2xl shadow-sm text-primary-600">
                                                        {selectedMeeting.mode === 'Virtual' ? <LinkIcon className="w-6 h-6" /> : <MapPinIcon className="w-6 h-6" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Venue / Link</p>
                                                        {selectedMeeting.mode === 'Virtual' ? (
                                                            <a href={selectedMeeting.meeting_link} target="_blank" rel="noopener noreferrer" className="font-bold text-primary-600 hover:underline break-all block">Join Virtual Meeting</a>
                                                        ) : (
                                                            <p className="font-bold text-slate-900 truncate">{selectedMeeting.location}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="p-3 bg-white rounded-2xl shadow-sm text-primary-600">
                                                        <CurrencyRupeeIcon className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Participation Fee</p>
                                                        <p className="font-bold text-slate-900">{parseFloat(selectedMeeting.charges) > 0 ? `₹${selectedMeeting.charges}` : 'Complimentary'}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">About the Meeting</h4>
                                                <div className="prose prose-slate max-w-none">
                                                    <p className="text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">{selectedMeeting.description}</p>
                                                </div>
                                            </div>

                                            {(!isAdmin && timeTab === 'upcoming') && (
                                                <div className="flex gap-4 pt-4">
                                                    {parseFloat(selectedMeeting.charges) > 0 && selectedMeeting.payment_link && (
                                                        <a
                                                            href={selectedMeeting.payment_link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl font-black text-center shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all"
                                                        >
                                                            Pay Securely
                                                        </a>
                                                    )}
                                                    <button
                                                        disabled={selectedMeeting.isRegistered}
                                                        onClick={() => handleRegister(selectedMeeting.id)}
                                                        className={twMerge(
                                                            "flex-1 py-4 rounded-2xl font-black text-lg shadow-lg transition-all",
                                                            selectedMeeting.isRegistered
                                                                ? "bg-emerald-50 text-emerald-600 cursor-default shadow-none"
                                                                : "bg-primary-600 text-white hover:bg-primary-700 shadow-primary-100"
                                                        )}
                                                    >
                                                        {selectedMeeting.isRegistered ? 'Already Registered' : 'Register Now'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
};

export default ChapterMeetings;
